import { useSeo } from "@/lib/seo";

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AppleSignInButton from "@/components/AppleSignInButton";

const MIN_PW = 8;

export default function Signup() {
  useSeo({
    title: "Join Vyrti — Free Barter Marketplace Account",
    description: "Create a free Vyrti account to post items, get smart-matched with traders near you, and swap without spending money.",
    path: "/signup",
  });
  const { register } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName]   = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (firstName.trim().length < 1) { toast.error("First name is required"); return; }
    if (password.length < MIN_PW)    { toast.error(`Password must be at least ${MIN_PW} characters`); return; }
    if (!agreed) { toast.error("Please agree to the Terms of Service to continue"); return; }
    setBusy(true);
    try {
      await register(firstName.trim(), lastName.trim(), email, password);
      toast.success("Welcome to Vyrti");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally { setBusy(false); }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="section-pad py-12 md:py-20 grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]" data-testid="signup-page">
      <div className="max-w-md w-full mx-auto">
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Join Vyrti</h1>
        <p className="text-zinc-500 mt-2">Start trading with people, not money.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5">First name</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input required className="input-text !pl-10" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} data-testid="signup-first-name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Last name</label>
              <div className="relative">
                <input className="input-text" placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} data-testid="signup-last-name" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="email" required className="input-text !pl-10" placeholder="you@vyrti.com" value={email} onChange={e => setEmail(e.target.value)} data-testid="signup-email" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input type="password" required minLength={MIN_PW} className="input-text !pl-10" placeholder={`Min ${MIN_PW} characters`} value={password} onChange={e => setPassword(e.target.value)} data-testid="signup-password" />
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">{MIN_PW}+ characters. Use letters, numbers, or symbols.</p>
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0654BA]" data-testid="signup-terms-checkbox" />
            <span className="text-xs text-zinc-600 leading-relaxed">
              I agree to the <Link to="/terms" target="_blank" className="text-[#0654BA] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#0654BA] hover:underline font-medium">Privacy Policy</Link>.
            </span>
          </label>
          <button type="submit" disabled={busy} className="btn-pill btn-accent w-full !py-3 disabled:opacity-50" data-testid="signup-submit">
            {busy ? "Creating…" : "Create account"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-zinc-400">
          <div className="flex-1 h-px bg-[#E2E2E2]" /> or <div className="flex-1 h-px bg-[#E2E2E2]" />
        </div>

        <div className="space-y-3">
          <button onClick={googleSignIn} type="button" className="btn-block w-full !py-3 bg-white text-zinc-900 border border-[#E2E2E2] hover:border-zinc-400" data-testid="signup-google">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <AppleSignInButton />
        </div>

        <p className="text-[11px] text-zinc-400 mt-3 text-center">
          By continuing with Google or Apple, you agree to our <Link to="/terms" className="text-[#0654BA] hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-[#0654BA] hover:underline">Privacy Policy</Link>.
        </p>

        <p className="text-sm text-zinc-500 mt-6 text-center">
          Already have an account? <Link to="/login" className="text-[#0654BA] hover:underline font-medium" data-testid="signup-login-link">Sign in</Link>
        </p>
      </div>

      <div className="hidden lg:block">
        <div className="grid grid-cols-2 gap-4">
          <img src="https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
          <img src="https://images.unsplash.com/photo-1473968512647-3e447244af8f?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] mt-12" />
          <img src="https://images.unsplash.com/photo-1591047139829-d91aecb6caea?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)] -mt-4" />
          <img src="https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=600" alt="" className="w-full aspect-square object-cover rounded-3xl shadow-[0_20px_40px_rgb(0,0,0,0.08)]" />
        </div>
      </div>
    </div>
  );
}
